import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, describeApiError, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = anonymous, object = authenticated
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (_) {
            tokenStore.clear();
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (identifier, password) => {
        try {
            const { data } = await api.post("/auth/login", { identifier, password });
            // New shape: { user, access_token, refresh_token }. Older shape was
            // just the user. Handle both for forward/backward compat.
            const isWrapped = data && typeof data === "object" && "user" in data && "access_token" in data;
            const userObj = isWrapped ? data.user : data;
            if (isWrapped) tokenStore.set(data.access_token, data.refresh_token);
            setUser(userObj);
            return { ok: true, user: userObj };
        } catch (e) {
            return { ok: false, error: describeApiError(e), status: e?.response?.status };
        }
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
        tokenStore.clear();
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
